import { useT } from '@transifex/react';
import type { JSX } from 'react';

import * as styles from 'pkg/config/styles';

import type { BirthDateParts } from 'pkg/birth_date';
import {
	birthDateBounds,
	birthDatePartValue,
	impossibleBirthDateMessage,
	isImpossibleBirthDate,
} from 'pkg/birth_date';

import type { InputChangeEvent } from 'components/form/inputs';
import { Field } from 'components/form/inputs/field';
import { Group } from 'components/form/inputs/group';
import Row from 'components/layout/row';

interface BirthDateProps extends BirthDateParts {
	label: string;
	onChange?: InputChangeEvent;
	// Fields get `<testid>.day|month|year`, the message `<testid>.error`
	testid?: string;
}

function partTestid(prefix: string | undefined, name: string) {
	if (prefix) {
		return `${prefix}.${name}`;
	}
}

export function BirthDate({
	label,
	year,
	month,
	day,
	onChange,
	testid,
}: BirthDateProps): JSX.Element {
	const t = useT();
	const bounds = birthDateBounds({ year, month });
	const impossible = isImpossibleBirthDate({ year, month, day });

	let errorMessage: string;

	if (impossible) {
		errorMessage = impossibleBirthDateMessage();
	}

	return (
		<Group
			label={label}
			hasError={impossible}
			errorMessage={errorMessage}
			testid={testid}>
			<Row spacing={styles.spacing._4}>
				<Field
					required
					name="day"
					type="number"
					placeholder={t('DD')}
					min={bounds.day.min}
					max={bounds.day.max}
					inputMode="numeric"
					value={birthDatePartValue(day)}
					onChange={onChange}
					testid={partTestid(testid, 'day')}
				/>
				<Field
					required
					name="month"
					type="number"
					placeholder={t('MM')}
					min={bounds.month.min}
					max={bounds.month.max}
					inputMode="numeric"
					value={birthDatePartValue(month)}
					onChange={onChange}
					testid={partTestid(testid, 'month')}
				/>
				<Field
					required
					name="year"
					type="number"
					placeholder={t('YYYY')}
					min={bounds.year.min}
					max={bounds.year.max}
					inputMode="numeric"
					value={birthDatePartValue(year)}
					onChange={onChange}
					testid={partTestid(testid, 'year')}
				/>
			</Row>
		</Group>
	);
}
